import { Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import { Collapse } from '@work4all/components/lib/components/collapse';
import { CheckboxRadioItem } from '@work4all/components/lib/input/checkbox-radio-item';
import { DateTimeInputPicker } from '@work4all/components/lib/input/date-time-input-picker';

import { ControllerPlus } from '../../../../../containers/mask-overlays/form-plus/controller-plus';
import { useFormContextPlus } from '../../../../../containers/mask-overlays/form-plus/use-form-context-plus';
import { ControlWrapper } from '../../../../../containers/mask-overlays/mask-overlay/components';
import { useNumberOfProjectHours } from '../hooks';
import { ProjectHourBillingOverlayFormValue } from '../types';

export const ProjectHourBillingOverlayBillableServices = () => {
  const { t } = useTranslation();
  const { control, watch } =
    useFormContextPlus<ProjectHourBillingOverlayFormValue>();

  const filters = watch('filters');
  const mode = watch('mode');
  const numberOfProjectHours = useNumberOfProjectHours(filters);

  return (
    <Collapse title={t('COMMON.BILLABLE_SERVICES')} defaultOpen>
      {filters?.projectTimeCodes?.length > 0 && (
        <ControllerPlus
          control={control}
          name="mode"
          render={({ field }) => {
            return (
              <Stack>
                <CheckboxRadioItem
                  label={t('COMMON.BILLABLE_SERVICES.MODE.PROJECT_TIMES', {
                    count: filters?.projectTimeCodes?.length,
                  })}
                  checked={field.value === 'projectTimes'}
                  control="radio"
                  onChange={() => {
                    field.onChange('projectTimes');
                  }}
                />
                <CheckboxRadioItem
                  label={t('COMMON.BILLABLE_SERVICES.MODE.PROJECT')}
                  checked={field.value === 'project'}
                  onChange={() => {
                    field.onChange('project');
                  }}
                  control="radio"
                />
              </Stack>
            );
          }}
        />
      )}
      <ControlWrapper columns={2}>
        <ControllerPlus
          control={control}
          name="filters"
          render={({ field }) => {
            return (
              <DateTimeInputPicker
                {...field}
                value={new Date(field.value.fromDate)}
                dateLabel={t('COMMON.START')}
                clearable={false}
                withTime={false}
                onChange={(value) => {
                  field.onChange({
                    ...field.value,
                    fromDate: DateTime.fromISO(
                      value.currentTarget.value
                    ).toFormat('yyyy-MM-dd'),
                  });
                }}
                disabled={mode === 'projectTimes'}
              />
            );
          }}
        />

        <ControllerPlus
          control={control}
          name="filters"
          render={({ field }) => {
            return (
              <DateTimeInputPicker
                {...field}
                value={new Date(field.value.toDate)}
                dateLabel={t('COMMON.END')}
                clearable={false}
                withTime={false}
                onChange={(value) => {
                  field.onChange({
                    ...field.value,
                    toDate: DateTime.fromISO(
                      value.currentTarget.value
                    ).toFormat('yyyy-MM-dd'),
                  });
                }}
                disabled={mode === 'projectTimes'}
              />
            );
          }}
        />
        <ControllerPlus
          control={control}
          name="filters"
          render={({ field }) => {
            return (
              <CheckboxRadioItem
                label={t('COMMON.BILLABLE_SERVICES.INCLUDE_UNBILLED')}
                checked={!field.value.onlyNotBilled}
                control="checkbox"
                onChange={() => {
                  field.onChange({
                    ...field.value,
                    onlyNotBilled: !field.value.onlyNotBilled,
                  });
                }}
              />
            );
          }}
        />

        <ControllerPlus
          control={control}
          name="filters"
          render={({ field: { value, ...rest } }) => (
            <CheckboxRadioItem
              {...rest}
              label={t('COMMON.ONLY_COMPLETED_TICKETS')}
              checked={value.onlyCompletedTickets}
              onChange={(e) => {
                rest.onChange({
                  ...value,
                  onlyCompletedTickets: e.target.checked,
                });
              }}
            />
          )}
        />

        {mode === 'project' && (
          <Typography sx={{ color: 'var(--text03)' }} padding={0}>
            {t('COMMON.INDIVIDUAL_SERVICES', { count: numberOfProjectHours })}
          </Typography>
        )}
      </ControlWrapper>
    </Collapse>
  );
};
